import { LinearGradient, LinearGradientProps } from 'expo-linear-gradient';
import { ReactNode } from 'react';
import {
  StyleProp,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import styled, { css } from 'styled-components';

export const Primary = {
  Purple: '#7B4BF6',
  Lime: '#E4FF5F',
  Magenta: '#EB2F96',
  FreshLime: '#7DFFB3',
};

export const Secondary = {
  DarkPurple: '#4A1FB8',
  LightPurple: '#A287F4',
  LighterPurple: '#E9E2FF',
  Teal: '#00FFCA',
  Red: '#FC344D',
  Green: '#82CF2E',
  Orange: '#F5A623',
  Blue: '#493AF3',
  Tiel: '#00CEA3',
};

export const Neutrals = {
  Black: 'black',
  Gray1: '#262626',
  Gray2: '#4A4A4A',
  Gray3: '#9498A2',
  Gray4: '#CED1D9',
  Gray5: '#F2F5FD',
  Gray6: 'rgba(0, 0, 0, 0.2)',
  Gray7: '#8c8c8c',
  Gray8: '#F3F3F4',
  Gray9: '#CED1D9',
  Gray10: '#F3F3F4',
  Gray11: '#CCC',
  Gray12: '#969696',
  White: 'white',
  Green1: '#12E6C8',
  Green2: '#5AB7DE',
  Purple1: '#A287F4',
  ThroughTeal: 'rgba(119, 255, 236, 0.25)',
  ThroughOrange: 'rgba(255, 132, 94, 0.25)',
  ThroughLime: 'rgba(203, 237, 38, 0.25 )',
  ThroughPurple: 'rgba(162, 135, 244, 0.25)',
  ThroughWhite: 'rgba(255, 255, 255, 0.25)',
  Transparent: 'transparent',
  Orange2: '#FF845E',
  LightTransparent: 'rgba(255,255,255, 0.7)',
};

export const Colors = {
  ...Primary,
  ...Secondary,
  ...Neutrals,
  DisabledSolid: Neutrals.Gray4,
  LayoutBG: '#f0f2f5',
};

function tl2brGradient(colors: string[], locations?: number[]) {
  return (
    props: Partial<LinearGradientProps> & {
      children?: ReactNode;
      style?: StyleProp<ViewStyle>;
    },
  ) => (
    <LinearGradient
      colors={props.colors ?? colors}
      start={props.start ?? [0, 0]}
      end={props.end ?? [1, 1]}
      style={props.style}
      locations={locations}
    >
      {props.children}
    </LinearGradient>
  );
}

export namespace FontFamily {
  /**
   * https://developer.mozilla.org/en-US/docs/Web/CSS/font-weight#common_weight_name_mapping
   */

  export const Thin = 'Montserrat-Hairline';
  export const Light = 'Montserrat-Light';
  export const Normal = 'Montserrat-Regular';
  export const Medium = 'Montserrat-Medium';
  export const SemiBold = 'Montserrat-SemiBold';
  export const Bold = 'Montserrat-Bold';
  export const Black = 'Montserrat-Black';

  export const weight100 = Thin;
  export const weight300 = Light;
  export const weight400 = Normal;
  export const weight500 = Medium;
  export const weight600 = SemiBold;
  export const weight700 = Bold;
  export const weight900 = Black;

  export const Sequel100Black65 = 'Sequel-100-Black-65';
  export const Sequel100Black75 = 'Sequel-100-Black-75';
  export const Sequel100Black45 = 'Sequel-100-Black-45';

  export const ThinStyle: TextStyle = {
    fontFamily: Thin,
    fontWeight: '100',
  };

  export const LightStyle: TextStyle = {
    fontFamily: Light,
    fontWeight: '300',
  };

  export const NormalStyle: TextStyle = {
    fontFamily: Normal,
    fontWeight: '400',
  };

  export const MediumStyle: TextStyle = {
    fontFamily: Medium,
    fontWeight: '500',
  };

  export const SemiBoldStyle: TextStyle = {
    fontFamily: SemiBold,
    fontWeight: '600',
  };

  export const BoldStyle: TextStyle = {
    fontFamily: Bold,
    fontWeight: '700',
  };

  export const BlackStyle: TextStyle = {
    fontFamily: Black,
    fontWeight: '900',
  };

  export const thinCss = css`
    font-family: ${Thin};
    font-weight: 100;
  `;

  export const lightCss = css`
    font-family: ${Light};
    font-weight: 300;
  `;

  export const normalCss = css`
    font-family: ${Normal};
    font-weight: 400;
  `;

  export const mediumCss = css`
    font-family: ${Medium};
    font-weight: 500;
  `;

  export const semiBoldCss = css`
    font-family: ${SemiBold};
    font-weight: 600;
  `;

  export const boldCss = css`
    font-family: ${Bold};
    font-weight: 700;
  `;

  export const blackCss = css`
    font-family: ${Black};
    font-weight: 900;
  `;

  export const thin = (style?: StyleProp<TextStyle>): StyleProp<TextStyle> =>
    style ? [ThinStyle, style] : ThinStyle;

  export const light = (style?: StyleProp<TextStyle>): StyleProp<TextStyle> =>
    style ? [LightStyle, style] : LightStyle;

  export const normal = (style?: StyleProp<TextStyle>): StyleProp<TextStyle> =>
    style ? [NormalStyle, style] : NormalStyle;

  export const medium = (style?: StyleProp<TextStyle>): StyleProp<TextStyle> =>
    style ? [MediumStyle, style] : MediumStyle;

  export const semiBold = (
    style?: StyleProp<TextStyle>,
  ): StyleProp<TextStyle> => (style ? [SemiBoldStyle, style] : SemiBoldStyle);

  export const bold = (style?: StyleProp<TextStyle>): StyleProp<TextStyle> =>
    style ? [BoldStyle, style] : BoldStyle;

  export const black = (style?: StyleProp<TextStyle>): StyleProp<TextStyle> =>
    style ? [BlackStyle, style] : BlackStyle;

  export const thinFlattened = (style?: StyleProp<TextStyle>) =>
    StyleSheet.flatten(style ? [ThinStyle, style] : ThinStyle);
  export const lightFlattened = (style?: StyleProp<TextStyle>) =>
    StyleSheet.flatten(style ? [LightStyle, style] : LightStyle);
  export const normalFlattened = (style?: StyleProp<TextStyle>) =>
    StyleSheet.flatten(style ? [NormalStyle, style] : NormalStyle);
  export const mediumFlattened = (style?: StyleProp<TextStyle>) =>
    StyleSheet.flatten(style ? [MediumStyle, style] : MediumStyle);
  export const semiBoldFlattened = (style?: StyleProp<TextStyle>) =>
    StyleSheet.flatten(style ? [SemiBoldStyle, style] : SemiBoldStyle);
  export const boldFlattened = (style?: StyleProp<TextStyle>) =>
    StyleSheet.flatten(style ? [BoldStyle, style] : BoldStyle);
  export const blackFlattened = (style?: StyleProp<TextStyle>) =>
    StyleSheet.flatten(style ? [BlackStyle, style] : BlackStyle);
}

export const Gradient1 = tl2brGradient(['#E139C8', '#6C8BE8']);
export const Gradient2 = tl2brGradient(['#E462EB', '#F7631C']);
export const Gradient3Colors = ['#12E6C8', '#A287F4'];
export const Gradient3 = tl2brGradient(Gradient3Colors);
export const Gradient5 = tl2brGradient(['#530C7A', 'black']);
export const Gradient6 = tl2brGradient(['#FF7347', '#7B4BF6']);
export const Gradient7 = tl2brGradient(['#530C7A', '#7B4BF6']);
export const LiveGradient = tl2brGradient(['#9AFFB0', '#CBED26']);

export const Shadow1Style = css({
  backgroundColor: 'white',
  boxShadow: '0 0 5px rgba(0, 0, 0, 0.1)',
  elevation: '1',
});

export const Shadow2Style = css({
  backgroundColor: 'white',
  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.15)',
  elevation: '2',
});

export const Shadow3Style = css({
  backgroundColor: 'white',
  boxShadow: `0 2px 8px ${Neutrals.Gray7}`,
  elevation: '2',
});

export const Shadow1 = styled(View)`
  ${Shadow1Style}
`;

export const Shadow2 = styled(View)`
  ${Shadow2Style}
`;

export const Shadow3 = styled(View)`
  ${Shadow3Style}
`;
